// @flow
import * as React from 'react';
import { type ContextRouter } from 'react-router-dom';
import ChannelAlert from './ChannelAlert';
import { requestModerationAlertUpdate } from 'containers/client/alertConfigActions';
import {
  type ClientProps,
  ClientContext,
} from 'containers/client/ClientProvider';
import type { ClientCatalog } from 'containers/client/types';
import ChannelListContainer from './ChannelListContainer';
import {
  channelByName,
  BrandChannelNames,
  RetailerChannelNames,
  DefaultModerationChannels,
  type Channel,
} from './ChannelInfo';
import { Map, List } from 'immutable';
import AsyncValues from 'lib/async-value';
import ChannelList from 'containers/channels/ChannelList';
import { EmptyCard } from 'components';
import { EmptyModerationAlertConfigRecord } from 'containers/client/alertConfigReducer';
import { connect } from 'react-redux';
import type { ModerationAlertConfig } from 'containers/client/types';
import { AuthContext } from 'ducks/auth-context';

// inject channel sagas
import { injectSaga, injectReducer } from 'store';
import saga from './saga';
import reducer from './reducer';

injectSaga(saga);
injectReducer(reducer);

type OwnProps = {
  ugctype: UgcType,
};
type Props = OwnProps & ContextRouter & { dispatch: Function };
type State = {
  activeChannel: ?Channel,
};

export class Channels extends React.PureComponent<Props, State> {
  state = {
    activeChannel: null,
  };

  showAlertManager = (channel: Channel) => {
    this.setState({
      activeChannel: channel,
    });
  };

  closeAlertManager = () => {
    this.setState({
      activeChannel: null,
    });
  };

  handleAlertManagerSubmit = (
    updatedConfig: ModerationAlertConfig,
    clientId: Id
  ) => {
    const { activeChannel } = this.state;

    if (!activeChannel) return;

    this.props.dispatch(
      requestModerationAlertUpdate(
        {
          ...updatedConfig,
          channel: activeChannel.name,
          client_id: +clientId,
        },
        {
          clientId: +clientId,
          channel: activeChannel.name,
        }
      )
    );
  };

  renderAlertManager = ({
    clientProps: { userProfiles, expertUserProfiles, clientId, alertConfigs },
    auth,
  }: {
    clientProps: ClientProps,
    auth: *,
  }) => {
    const { activeChannel } = this.state;
    if (!activeChannel || !clientId) return null;

    const isAssignedChannel =
      activeChannel.name === 'AssignedQnA' ||
      activeChannel.name === 'AssignedReview';

    const userProfilesForChannel = isAssignedChannel
      ? [
          // This ensures that the logged in user can subscribe themselves to alerts
          {
            email: auth.email,
            name: auth.name,
            profile_id: auth.profile_id,
          },
        ]
      : userProfiles;

    if (!Array.isArray(userProfilesForChannel)) return null;

    const { config: currentAlertConfig, changeStatus } =
      alertConfigs instanceof Map
        ? alertConfigs.get(activeChannel.name, EmptyModerationAlertConfigRecord)
        : EmptyModerationAlertConfigRecord;

    return (
      <ChannelAlert
        title={activeChannel.label}
        // $FlowFixMe better record typing
        onSubmit={config => this.handleAlertManagerSubmit(config, clientId)}
        onClose={this.closeAlertManager}
        userProfiles={userProfilesForChannel}
        currentAlertConfig={currentAlertConfig}
        changeStatus={changeStatus}
      />
    );
  };

  render() {
    const { ugctype, ...routerProps } = this.props;
    return (
      <AuthContext.Consumer>
        {auth => (
          <ClientContext.Consumer>
            {(clientProps: ClientProps) => {
              if (!clientProps) return null;

              const {
                userProfiles,
                clientId,
                properties,
                catalogs,
              } = clientProps;
              const canManageAlerts = Array.isArray(userProfiles);
              const props = {
                ...routerProps,
                ...(canManageAlerts === true && {
                  onChannelSelect: this.showAlertManager,
                }),
              };

              if (
                properties === AsyncValues.Pending ||
                catalogs === AsyncValues.Pending
              ) {
                return routerProps.match.isExact === true ? (
                  <PendingChannelList />
                ) : (
                  <EmptyCard message="Loading Channels..." />
                );
              }

              if (properties instanceof Map && catalogs instanceof List) {
                const allChannels = getRelevantChannels(
                  properties,
                  catalogs,
                  ugctype
                );
                return (
                  <React.Fragment>
                    <ChannelListContainer
                      {...props}
                      profileId={auth.profile_id}
                      channels={allChannels}
                      clientId={clientId}
                      properties={properties}
                      channelType={ugctype}
                    />
                    <div data-translate="true">
                      {this.renderAlertManager({ clientProps, auth })}
                    </div>
                  </React.Fragment>
                );
              }

              return (
                <EmptyCard message="An Error occurred while loading your channels." />
              );
            }}
          </ClientContext.Consumer>
        )}
      </AuthContext.Consumer>
    );
  }
}

function getModerationChannels(ugcType) {
  return DefaultModerationChannels.reduce((acc, channelName) => {
    if (channelByName.hasOwnProperty(channelName)) {
      const channel = channelByName[channelName];

      if (channel.ugctype === ugcType) {
        acc.push(channel);
      }
    }
    return acc;
  }, []);
}

function getBrandEngageChannels(brandEngageType: string, ugcType) {
  const brandChannelNames =
    brandEngageType === 'brand'
      ? BrandChannelNames
      : brandEngageType === 'retailer'
      ? RetailerChannelNames
      : [];

  return brandChannelNames.reduce((acc, channelName) => {
    if (channelByName.hasOwnProperty(channelName)) {
      const channel = channelByName[channelName];

      if (channel.ugctype === ugcType && channel.type === 'brand_engage') {
        acc.push(channel);
      }
    }

    return acc;
  }, []);
}

function getRelevantChannels(
  properties: *,
  catalogs: List<ClientCatalog>,
  ugctype
) {
  const brandEngageType = properties.get('BRAND_ENGAGE_TYPE');
  const showDefaultModerationChannels = properties.get(
    'SHOW_CLIENT_MODERATION_CHANNELS'
  );
  const isBrand = brandEngageType === 'brand';

  if (isBrand && showDefaultModerationChannels === false) {
    return getBrandEngageChannels(brandEngageType, ugctype);
  }

  const l2AutoApproveLevel = properties.get('LEVEL2_AUTO_APPROVE_LEVEL');
  const clientModerationRequired =
    typeof l2AutoApproveLevel === 'number' && l2AutoApproveLevel > 0;
  const hasSocialCollection =
    catalogs.filter(catalog => catalog.social_collection_property_id).size > 0;

  const moderationChannels = getModerationChannels(ugctype);
  if (
    clientModerationRequired &&
    ugctype === channelByName.AwaitingClientModeration.ugctype
  ) {
    moderationChannels.push(channelByName.AwaitingClientModeration);
  }
  if (
    hasSocialCollection &&
    ugctype === channelByName.SocialCollection.ugctype
  ) {
    moderationChannels.push(channelByName.SocialCollection);
  }

  return [
    ...moderationChannels,
    ...getBrandEngageChannels(brandEngageType, ugctype),
  ];
}

function PendingChannelList() {
  return (
    <ChannelList>
      <ChannelList.ItemPending />
      <ChannelList.ItemPending />
      <ChannelList.ItemPending />
      <ChannelList.ItemPending />
    </ChannelList>
  );
}

export default connect()(Channels);



// WEBPACK FOOTER //
// ./src/containers/channels/Channels.js